import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Box from '@mui/material/Box'
import { alpha } from '@mui/material/styles'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { PERMISSIONS, airworthinessLight, holdRestrictions, type AircraftDefect, type DefectRestriction } from '@jupi/shared'
import { ApiError, RemoteContent, Title, getClubTimezone, useAuth, useSnackbar } from '@jupi/ui'
import { FlightLogDate } from '../components/flightLog/FlightLogCard'
import { useAircraft, useAircraftDefects, useReportDefect } from '../api/queries'

export function RestrictionChips({ restriction }: { restriction: DefectRestriction[] }) {
  const { t } = useTranslation()
  return restriction.map((r) => (
    <Chip key={r} size="small" color={r === 'grounded' ? 'error' : 'warning'} label={t(`aircraftDefect.restrictions.${r}`)} />
  ))
}

export default function AircraftDefectsPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  // ?registration=OH-CAM, from the fleet page's status chip.
  const [params, setParams] = useSearchParams()
  const registration = params.get('registration')?.toUpperCase() || undefined
  const { data, isLoading, error } = useAircraftDefects(registration)
  const [reporting, setReporting] = useState(false)

  return (
    <Box>
      <Title label={t('aircraftDefect.titlePlural')}>
        {can(PERMISSIONS.defectReport) ? (
          <Button variant="contained" startIcon={<Icon icon="mdi:plus" />} onClick={() => setReporting(true)}>
            {t('aircraftDefect.report')}
          </Button>
        ) : null}
      </Title>
      {registration ? (
        <Chip
          sx={{ mb: 2 }}
          label={t('aircraftDefect.filteredBy', { registration })}
          onDelete={() => setParams({})}
          deleteIcon={<Icon icon="mdi:close" />}
        />
      ) : null}
      <RemoteContent isLoading={isLoading} error={error}>
        <Stack spacing={2}>
          {data?.mine.map((defect) => <DefectCard key={defect.defectId} defect={defect} pending />)}
          {data?.published.length === 0 && data.mine.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              {t('aircraftDefect.none')}
            </Typography>
          ) : null}
          {data?.published.map((defect) => <DefectCard key={defect.defectId} defect={defect} />)}
        </Stack>
      </RemoteContent>
      <ReportDialog key={registration ?? 'all'} open={reporting} onClose={() => setReporting(false)} defaultRegistration={registration} />
    </Box>
  )
}

function ReportDialog({
  open,
  onClose,
  defaultRegistration,
}: {
  open: boolean
  onClose: () => void
  defaultRegistration?: string
}) {
  const { t } = useTranslation()
  const { showSnackbar } = useSnackbar()
  const { data: aircraft = [] } = useAircraft()
  const report = useReportDefect()
  const [registration, setRegistration] = useState(defaultRegistration ?? '')
  const [message, setMessage] = useState('')
  const [photo, setPhoto] = useState<File | null>(null)

  const submit = async () => {
    try {
      await report.mutateAsync({ registration, message, photo })
      showSnackbar(t('aircraftDefect.reportedOk'))
      setMessage('')
      setPhoto(null)
      onClose()
    } catch (err) {
      showSnackbar(err instanceof ApiError ? err.message : t('common.error'), { severity: 'error' })
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('aircraftDefect.report')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField select required label={t('aircraftDefect.aircraft')} value={registration} onChange={(e) => setRegistration(e.target.value)}>
            {aircraft.map((plane) => (
              <MenuItem key={plane.registration} value={plane.registration}>
                {plane.registration}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            required
            multiline
            minRows={3}
            label={t('aircraftDefect.message')}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            slotProps={{ htmlInput: { maxLength: 2000 } }}
          />
          <Button component="label" variant="outlined" sx={{ alignSelf: 'flex-start' }}>
            {photo ? photo.name : t('aircraftDefect.photo')}
            <input
              hidden
              type="file"
              accept="image/jpeg,image/png,application/pdf"
              onChange={(e) => setPhoto(e.target.files?.[0] ?? null)}
            />
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" disabled={report.isPending || !registration || !message.trim()} onClick={submit}>
          {t('aircraftDefect.report')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// Published defects take their severity's colour: red grounds the aircraft,
// amber is a warning. Anything not yet published (the reporter's own
// pending or held reports) stays neutral and says where it stands.
function DefectCard({ defect, pending = false }: { defect: AircraftDefect; pending?: boolean }) {
  const { t } = useTranslation()
  const light = airworthinessLight([defect])
  const colour = light === 'aog' ? 'error' : light === 'restricted' ? 'warning' : null

  return (
    <Card>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 0.5,
            mb: 1,
            borderRadius: 1,
            bgcolor: (theme) => (colour ? alpha(theme.palette[colour].main, 0.15) : 'action.hover'),
          }}
        >
          <Typography variant="subtitle2" component="p" sx={{ fontWeight: 700 }}>
            {defect.registration}
          </Typography>
          {colour ? (
            <Stack direction="row" spacing={0.5}>
              {defect.status === 'held' ? (
                <Chip size="small" variant="outlined" label={t('aircraftDefect.holdItem')} sx={{ bgcolor: 'background.paper' }} />
              ) : null}
              <Chip
                size="small"
                color={colour}
                icon={<Icon icon={light === 'aog' ? 'mdi:hand-front-left' : 'mdi:alert-outline'} />}
                label={t(`aircraftDefect.airworthiness.${light}`)}
                sx={{ fontWeight: 700 }}
              />
            </Stack>
          ) : (
            <Chip size="small" variant="outlined" label={t(`aircraftDefect.status.${defect.status}`)} />
          )}
        </Box>
        <Stack direction="row" spacing={2}>
          <FlightLogDate date={defect.reportedAt} timeZone={getClubTimezone()} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
              {defect.message}
            </Typography>
            <Stack direction="row" sx={{ gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
              <RestrictionChips restriction={defect.status === 'held' ? holdRestrictions(defect) : defect.restriction} />
            </Stack>
            {pending ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {t('aircraftDefect.pendingHint')}
              </Typography>
            ) : null}
            {defect.reportedByName ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {t('aircraftDefect.reportedBy')}: {defect.reportedByName}
              </Typography>
            ) : null}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )
}
